import { Component, effect, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IdentityStore, IdentityStoreType } from '@appState/identity/identity.store';
import { withDefinedSignal } from '@highlander/common/helpers/signal-helpers';
import { ProductRequest, RequestsToggleOptions } from '@models/products-and-orders/requests';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { CuiTableModule, DialogService, formatDate } from 'cui-components';
import { startOfDay } from 'date-fns';
import {
  BcDevRequestsStore,
  BcDevRequestsStoreType,
} from '../../../app-state/products-and-orders/requests/bc-dev-requests/bc-dev-requests.store';
import { RequestsStore, RequestsStoreType } from '../../../app-state/products-and-orders/requests/requests.store';
import { AddRequestDialogComponent } from './dialogs/add-request-dialog.component';

export const REQUESTS_TABLE_ID = 'requests-table';
@Component({
  imports: [CuiTableModule, RouterModule],
  selector: 'hui-requests',
  template: ` <cui-table
      [tableId]="REQUESTS_TABLE_ID"
      id="requests-table"
      [columnDefs]="columnDefs"
      [isLoading]="requestsStore.isLoading()"
      [selectedRowId]="requestsStore.selectedRequestId()"
      [rowData]="requestsStore.requests()"
      [toggleDefaultOption]="toggleValue()"
      [toggleOptions]="toggleOptions"
      [navigationFn]="navigateToRequest"
      [gridOptions]="gridOptions"
      [ctaText]="hasCreatePermission ? 'Add' : ''"
      (ctaClicked)="onAddClick()"
      (toggleClicked)="onToggleClick($event)"
    ></cui-table>

    <router-outlet></router-outlet>`,
  styleUrls: ['./requests.component.scss'],
})
export class RequestsComponent {
  requestsStore: RequestsStoreType = inject(RequestsStore);
  bcDevRequestsStore: BcDevRequestsStoreType = inject(BcDevRequestsStore);
  private identityStore: IdentityStoreType = inject(IdentityStore);
  private dialogService: DialogService = inject(DialogService);

  toggleOptions: RequestsToggleOptions[] = [RequestsToggleOptions.Active, RequestsToggleOptions.All];
  REQUESTS_TABLE_ID = REQUESTS_TABLE_ID;
  toggleValue = this.requestsStore.toggleOption;
  hasCreatePermission = this.identityStore.hasAllPermissions(['Portal.Admin.ProductsAndOrders.Requests.Create']);

  gridApi: GridApi | undefined;

  gridOptions = {
    onGridReady: (event: GridReadyEvent) => {
      this.gridApi = event.api;
    },
  };

  columnDefs: ColDef<ProductRequest>[] = [
    {
      field: 'partner',
      minWidth: 150,
      valueGetter: params => params.data?.partner?.name ?? 'N/A',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'product',
      minWidth: 150,
      valueGetter: params => params.data?.product?.name ?? 'N/A',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'customer',
      minWidth: 150,
      valueGetter: params => params.data?.customer?.name ?? 'N/A',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'description',
      minWidth: 200,
    },
    {
      headerName: 'Request date',
      valueGetter: params => params.data?.submissionDate ?? params.data?.lastUpdatedBy?.date,
      valueFormatter: params => (params.value ? formatDate(new Date(params.value), 'yyyy-MM-dd') : ''),
      minWidth: 120,
    },
    {
      field: 'expirationDate',
      minWidth: 150,
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        label: 'Filter by date',
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        inRangeInclusive: true,
      },
      valueFormatter: params => (params.value ? formatDate(new Date(params.value), 'yyyy-MM-dd') : ''),
      filterValueGetter: ({ data }) => data?.expirationDate && startOfDay(new Date(data.expirationDate)),
    },
    {
      field: 'lastUpdatedBy',
      minWidth: 150,
      valueGetter: params => params.data?.lastUpdatedBy?.email ?? '',
    },
    {
      field: 'status',
      cellClassRules: {
        ['xs-icon']: () => true,
      },
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
      minWidth: 100,
      cellRenderer: 'cuiTableCellColoredChipRenderer',
      cellRendererParams: ({ data }: ICellRendererParams<ProductRequest>) => {
        const isOnHold = data?.status === 'OnHold';

        return {
          value: {
            cellValue: data?.status,
            iconName: isOnHold ? 'info' : undefined,
            iconTooltip: isOnHold ? data?.statusDetails : undefined,
          },
        };
      },
    },
    {
      field: 'partner.membershipLevel',
      headerName: 'Membership level',
      valueGetter: params => params.data?.partner?.membershipLevel ?? '',
      minWidth: 150,
    },
    {
      field: 'partner.regionName',
      headerName: 'Region',
      valueGetter: params => params.data?.partner?.regionName ?? '',
      minWidth: 90,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'partner.id',
      hide: true,
    },
    {
      field: 'partner.regionId',
      hide: true,
    },
    {
      field: 'customer.id',
      hide: true,
    },
    {
      field: 'product.id',
      hide: true,
    },
  ];

  constructor() {
    if (!this.requestsStore.isLoading()) {
      this.requestsStore.loadRequests(this.requestsStore.toggleOption());
    }

    effect(() => {
      withDefinedSignal(this.requestsStore.selectedRequestId, selectedRequestId => {
        if (this.gridApi) {
          const rowNode = this.gridApi.getRowNode(selectedRequestId);
          if (rowNode) {
            setTimeout(() => this.gridApi!.ensureNodeVisible(rowNode), 0);
          }
        }
      });
    });
  }

  onToggleClick(toggle: RequestsToggleOptions): void {
    this.requestsStore.loadRequests(toggle);
  }

  onAddClick(): void {
    this.dialogService.openDialog<undefined, undefined>(AddRequestDialogComponent, undefined);
  }

  navigateToRequest = (request: ProductRequest): Promise<boolean> => {
    this.bcDevRequestsStore.resetState();
    return this.requestsStore.goToRequestActions(request.id);
  };
}
